import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  NOT_CREDITED_LABEL,
  REVENUE_BASES,
  monthLabel,
  presetRange,
  rangeLabel,
  type ReportPresetKey,
  type RevenueBasis,
  type RevenuePage as RevenuePagePayload,
} from '@servwell/shared';
import { useTRPC } from '../../../lib/trpc';
import { KpiCard, KpiGrid, PageHeader, PageRoot, Toolbar } from '../../ui/Page';
import { BackCrumb } from '../../ui/Detail';
import { REPORT_PERIODS, useUrlTab } from '../../ui/pageTabs';
import { Notice } from '../../ui/Badge';
import { Skeleton } from '../../ui/Skeleton';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
  unresolved,
} from '../../ui/QueryStates';
import { WithOrgClock } from '../../ui/WithOrgClock';
import { IconReports } from '../../icons';
import { money } from '../../money';
import { PeriodPresets } from '../PeriodPresets';
import { ByServiceType } from './ByServiceType';
import { ByPerson, ByPersonWithheld } from './ByPerson';
import { RevenueChart, RevenueChartSkeleton } from './RevenueChart';
import { RevenueDrill, figureCents, type Drill } from './RevenueDrill';
import { BasisSwitch } from './BasisSwitch';
import { DownloadCsv } from './DownloadCsv';
import { BASIS_WORDS } from './basisWords';
import { byPersonShown } from './onScreen';

/**
 * The Revenue page (ops insights Phase 2): what the shop invoiced, or collected, in a period,
 * and what kind of work earned it. Reached from the top of Money in the Reports menu, at
 * `/reports/revenue`.
 *
 * A thin client: the server (`report.revenue`) decides every figure and every row, the totals
 * under the table included, and the same days a year earlier, and this draws them. The browser
 * picks the period's DATES, from the shop's zone (`presetRange` inside `WithOrgClock`, as the
 * Reports page does), and works out each row's share of the total (`sharePct`) and each change
 * (`yearEarlierChange`, through `Change`); the rest is formatting.
 *
 * The period and the basis live in the address, `?period=` and `?basis=`, on a tabbed page's
 * contract (`revenueSearch` on the route, `useUrlTab` here, under those names): This month and
 * Invoiced are the defaults and are never written into a link, junk reads as the default, and a
 * click replaces the address rather than adding to the history.
 *
 * The Invoiced | Collected switch (ticket 07, `BasisSwitch`) changes every number on the page:
 * the server answers on the basis asked for, and the page draws that answer in its basis's
 * words (`BASIS_WORDS`), the headline in the colour the server chose for it (`totalTone`: under
 * Collected, green only above zero). A figure below zero is printed with its sign; no bar draws
 * a negative length (the share bar, the mix bar and the chart each state it instead).
 *
 * Built on prototype 201 A, in the order the spec gives: the definition, the period, the four
 * figures (the first three against the same days a year earlier), the chart of the twelve
 * months to the period's last day (`RevenueChart`), by service type beside by person (stacked
 * below `lg`), the caveats. Download CSV (ticket 08) ends the controls.
 *
 * Every figure of the period opens the invoices behind it in the side panel (`RevenueDrill`,
 * ticket 06): the Invoiced card, a month of the chart, a service type row, a person and "Not
 * credited". The page holds which one is open (the address does not, spec Out of Scope), and
 * the panel is titled with the page's own name for the figure. Year-earlier figures and
 * averages open nothing.
 *
 * By person is drawn from what the SERVER sent, never from the caller's role, by the rule its CSV
 * follows too (`byPersonShown`): the section when its people came and nothing withholds them,
 * and otherwise the server's own `withheld` notice where it would be, with by service type
 * taking the full width.
 */

export function RevenuePage() {
  return (
    <WithOrgClock what="your revenue" icon={IconReports} title="Revenue">
      {(tz) => <RevenueFromUrl tz={tz} />}
    </WithOrgClock>
  );
}

/** The period and the basis from the address, the period resolved to the shop's days. */
function RevenueFromUrl({ tz }: { tz: string }) {
  const [period, setPeriod] = useUrlTab(REPORT_PERIODS, 'period');
  const [basis, setBasis] = useUrlTab(REVENUE_BASES, 'basis');
  const range = useMemo(() => presetRange(period, tz), [period, tz]);
  return (
    <RevenueReport
      period={period}
      range={range}
      onPeriod={setPeriod}
      basis={basis}
      onBasis={setBasis}
    />
  );
}

/**
 * The page for one period's dates, on one basis. Exported so a test can draw it over a seeded
 * answer.
 */
export function RevenueReport({
  period,
  range,
  onPeriod,
  basis,
  onBasis,
}: {
  period: ReportPresetKey;
  range: { from: string; to: string };
  onPeriod: (next: ReportPresetKey) => void;
  basis: RevenueBasis;
  onBasis: (next: RevenueBasis) => void;
}) {
  const trpc = useTRPC();
  const page = useQuery(trpc.report.revenue.queryOptions({ ...range, basis }));
  const phase = shownState(page, page.data);
  const days = rangeLabel(range.from, range.to);
  // The figure whose invoices are open in the side panel, if any.
  const [drill, setDrill] = useState<Drill | null>(null);

  return (
    <PageRoot>
      <BackCrumb to="/reports" label="Reports" />
      <PageHeader title="Revenue" sub={days} />
      <p className="text-sm text-muted-foreground">{BASIS_WORDS[basis].definition}</p>
      <Toolbar>
        <PeriodPresets value={period} onChange={onPeriod} />
        <BasisSwitch value={basis} onChange={onBasis} />
        <DownloadCsv data={page.data} />
      </Toolbar>
      <StaleNotice
        show={refreshFailed(page, page.data)}
        onRetry={() => void page.refetch()}
      />
      {phase === 'ready' && page.data ? (
        <RevenueBody data={page.data} days={days} onDrill={setDrill} />
      ) : phase === 'loading' ? (
        <RevenueSkeleton />
      ) : (
        <UnresolvedState
          state={unresolved(phase)}
          what="revenue"
          icon={IconReports}
          error={page.error}
          onRetry={() => void page.refetch()}
        />
      )}
      <RevenueDrill
        drill={drill}
        pageCents={drill && page.data ? figureCents(page.data, drill.figure) : null}
        range={range}
        basis={basis}
        onClose={() => setDrill(null)}
      />
    </PageRoot>
  );
}

function RevenueBody({
  data,
  days,
  onDrill,
}: {
  data: RevenuePagePayload;
  days: string;
  /** Open a figure's invoices in the side panel. */
  onDrill: (drill: Drill) => void;
}) {
  const { kpis } = data;
  const words = BASIS_WORDS[data.basis];
  // What every change is against, as the server resolved it: "Sep 1–28, 2025".
  const yearEarlierDays = rangeLabel(data.yearEarlier.from, data.yearEarlier.to);
  const { total, jobs, averageJob } = kpis;
  // Beside by person when there is one, full width when it was withheld.
  const byType = (
    <ByServiceType
      rows={data.byServiceType}
      kpis={kpis}
      words={words}
      days={days}
      yearEarlierDays={yearEarlierDays}
      onOpen={(row) =>
        onDrill({ figure: { kind: 'service-type', key: row.key }, title: row.label })
      }
    />
  );
  return (
    <>
      <KpiGrid>
        {/* The colour is the server's (`totalTone`): Collected is green only above zero. The
            sign is the number's own: a period whose refunds outran what arrived reads −. */}
        <KpiCard
          label={words.label}
          value={money(total.cents)}
          tone={kpis.totalTone}
          onOpen={() => onDrill({ figure: { kind: 'total' }, title: words.label })}
          comparison={{
            now: total.cents,
            yearEarlier: total.yearEarlierCents,
            against: yearEarlierDays,
          }}
        />
        <KpiCard
          label={words.jobs}
          value={jobs.count.toLocaleString('en-US')}
          comparison={{
            now: jobs.count,
            yearEarlier: jobs.yearEarlierCount,
            against: yearEarlierDays,
          }}
        />
        {/* `money(null)` is the dash: an average over no jobs is not $0.00, and its line says
            "no jobs in the period" rather than a fall to nothing. */}
        <KpiCard
          label={words.averageJob}
          value={money(averageJob.cents)}
          comparison={{
            now: averageJob.cents,
            yearEarlier: averageJob.yearEarlierCents,
            against: yearEarlierDays,
          }}
        />
        <KpiCard
          label={words.otherBasis}
          value={money(kpis.otherBasisCents)}
          basis={words.otherBasisNote}
        />
      </KpiGrid>

      <RevenueChart
        trend={data.trend}
        to={data.range.to}
        label={words.label}
        onOpenMonth={(m) =>
          onDrill({
            figure: { kind: 'month', month: m.month },
            title: `${words.label} in ${monthLabel(m.month)}`,
          })
        }
      />

      {byPersonShown(data) ? (
        <div className="grid gap-6 lg:grid-cols-2 [&>*]:min-w-0">
          {byType}
          <ByPerson
            byPerson={data.byPerson}
            words={words}
            days={days}
            onOpenPerson={(row) =>
              onDrill({ figure: { kind: 'person', userId: row.userId }, title: row.name })
            }
            onOpenNotCredited={() =>
              onDrill({ figure: { kind: 'not-credited' }, title: NOT_CREDITED_LABEL })
            }
          />
        </div>
      ) : (
        <>
          {byType}
          <ByPersonWithheld withheld={data.withheld} />
        </>
      )}

      {/* What the numbers can't tell you, next to them, in the order the server gives. */}
      <Notice tone="info">
        <ul className="space-y-1">
          {data.caveats.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      </Notice>
    </>
  );
}

/** The page's shape while it loads: four figures, the chart and a table. */
function RevenueSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true">
      <KpiGrid>
        <Skeleton className="h-20" />
        <Skeleton className="h-20" />
        <Skeleton className="h-20" />
        <Skeleton className="h-20" />
      </KpiGrid>
      <RevenueChartSkeleton />
      <Skeleton className="h-48" />
    </div>
  );
}
